/**
 * Sell › SOWs: the list's filters, sort and view, pure (tests/unit/sowListView.test.ts).
 *
 * A page of SOWs is a table, not a stack of cards: past TABLE_THRESHOLD the list opens as a
 * compact, sortable table (the viewer's choice is remembered). Status is one select next to
 * search; people is another: everyone, mine, or one lead or team member by name.
 */

export interface SowListRecord {
  id: string;
  companyId?: string;
  companyName?: string;
  name?: string;
  description?: string;
  status?: string;
  pricing?: { type?: string; amount?: number; hourlyRate?: number; estimatedHours?: number; basePrice?: number; markupPercent?: number; markupAmount?: number };
  timeline?: { startDate?: string; endDate?: string };
  deliverables?: Array<{ status?: string }>;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  ownerId?: string;
  /** The firm user accountable for the engagement; the owner or creator until one is named. */
  engagementLeadId?: string;
  engagementLeadName?: string;
  /** Names typed on the SOW (SOWGenerator's Project Team section). */
  teamMembers?: string[];
  /** Firm user ids assigned to the engagement. */
  assignedConsultants?: string[];
  /** Configuration items in the SOW's scope (sowListCore.configItemCountFor); a badge on the list. */
  configItemCount?: number;
  /** The intake the SOW came from, when it did ("From intake" badge). */
  originIntakeId?: string | null;
}

/** Executed SOWs: a contract. Deleting one asks twice as hard (the server allows it: the recourse for a wrong upload). */
export const FROZEN_SOW_STATUSES: readonly string[] = ['approved', 'active', 'completed'];

/** Who may delete a SOW from the list (as the server's delete route): its creator, a firm admin, a system admin. */
export function canDeleteSow(sow: SowListRecord, viewer: { id?: string; role?: string; roles?: string[]; isAdmin?: boolean; isCFAdmin?: boolean } | null | undefined): boolean {
  if (!viewer) return false;
  const roles = [viewer.role, ...(Array.isArray(viewer.roles) ? viewer.roles : [])];
  if (viewer.isAdmin === true || roles.includes('systemAdmin') || roles.includes('system_admin')) return true;
  if (viewer.isCFAdmin === true || roles.some((r) => r === 'cfAdmin' || r === 'consulting_firm_admin' || r === 'firm_admin' || r === 'consultingFirmAdmin')) return true;
  return !!viewer.id && sow.createdBy === viewer.id;
}

/** The question before deleting: plain for drafts, a warning for an executed SOW. */
export function deleteSowPrompt(sow: SowListRecord): string {
  const name = sow.name && sow.name !== 'Untitled SOW' ? `"${sow.name}"` : 'this SOW';
  return FROZEN_SOW_STATUSES.includes(sow.status ?? '')
    ? `${name} for ${sow.companyName ?? 'this client'} is ${sow.status}: it is the signed contract. Delete it only if the wrong document was attached. Delete it?`
    : `Delete ${name} for ${sow.companyName ?? 'this client'}? This cannot be undone.`;
}

export interface FirmUserLite { id: string; name?: string; email?: string }
export interface ViewerLite { id?: string; name?: string; email?: string }

export const TABLE_THRESHOLD = 6;
export type SowView = 'cards' | 'table';
export type SowSort = 'updated' | 'client' | 'status' | 'value' | 'start';
export type PersonFilter = 'everyone' | 'me' | string;

export const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
];

export const SORT_OPTIONS: Array<{ value: SowSort; label: string }> = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'client', label: 'Client A to Z' },
  { value: 'status', label: 'Status' },
  { value: 'value', label: 'Value, high to low' },
  { value: 'start', label: 'Start date, newest' },
];

const STATUS_ORDER = ['draft', 'pending_approval', 'approved', 'active', 'completed', 'rejected', 'archived'];

/** The list opens as a table once it is a page long. */
export function defaultView(count: number): SowView {
  return count > TABLE_THRESHOLD ? 'table' : 'cards';
}

/** The SOW's value as one number: the fixed amount, else rate × hours, else 0. */
export function sowValue(sow: SowListRecord): number {
  const p = sow.pricing ?? {};
  if (Number(p.amount) > 0) return Number(p.amount);
  if (Number(p.hourlyRate) > 0 && Number(p.estimatedHours) > 0) return Number(p.hourlyRate) * Number(p.estimatedHours);
  return 0;
}

function nameOf(id: string | undefined, users: FirmUserLite[]): string | null {
  if (!id) return null;
  const u = users.find((x) => x.id === id);
  return u?.name || u?.email || null;
}

/** Who is on a SOW: the lead (the engagement lead, else owner, else creator) and the team, as names where the firm's user list knows them. */
export function sowPeople(sow: SowListRecord, users: FirmUserLite[] = []): { leadId: string | null; leadName: string | null; team: string[] } {
  const leadId = sow.engagementLeadId || sow.ownerId || sow.createdBy || null;
  const leadName = nameOf(leadId ?? undefined, users) || (sow.engagementLeadId && sow.engagementLeadName ? sow.engagementLeadName : null);
  const team = new Set<string>();
  for (const m of sow.teamMembers ?? []) if (typeof m === 'string' && m.trim()) team.add(m.trim());
  for (const id of sow.assignedConsultants ?? []) { const n = nameOf(id, users); if (n) team.add(n); }
  return { leadId, leadName, team: [...team] };
}

/** Is the viewer on this SOW: its owner or creator, assigned to it, or named on its team. */
export function isMine(sow: SowListRecord, viewer: ViewerLite | null | undefined): boolean {
  if (!viewer) return false;
  const id = viewer.id ? String(viewer.id) : null;
  if (id && (sow.engagementLeadId === id || sow.ownerId === id || sow.createdBy === id || (sow.assignedConsultants ?? []).includes(id))) return true;
  const me = [viewer.name, viewer.email].filter(Boolean).map((s) => String(s).trim().toLowerCase());
  return (sow.teamMembers ?? []).some((m) => me.includes(String(m).trim().toLowerCase()));
}

/** The names a people filter can pick: every lead and team member across the list, sorted. */
export function peopleOptions(sows: SowListRecord[], users: FirmUserLite[] = []): string[] {
  const names = new Set<string>();
  for (const sow of sows) {
    const { leadName, team } = sowPeople(sow, users);
    if (leadName) names.add(leadName);
    for (const t of team) names.add(t);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

export interface SowFilters {
  search?: string;
  status?: string;
  person?: PersonFilter;
}

export function filterSows(sows: SowListRecord[], filters: SowFilters, ctx: { users?: FirmUserLite[]; viewer?: ViewerLite | null } = {}): SowListRecord[] {
  const q = (filters.search ?? '').trim().toLowerCase();
  const status = filters.status ?? 'all';
  const person = filters.person ?? 'everyone';
  const users = ctx.users ?? [];
  return sows.filter((sow) => {
    if (status !== 'all' && (sow.status ?? 'draft') !== status) return false;
    if (person === 'me' && !isMine(sow, ctx.viewer)) return false;
    if (person !== 'everyone' && person !== 'me') {
      const { leadName, team } = sowPeople(sow, users);
      if (leadName !== person && !team.includes(person)) return false;
    }
    if (q) {
      const { leadName, team } = sowPeople(sow, users);
      const hay = [sow.name, sow.companyName, sow.description, leadName, ...team].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function sortSows(sows: SowListRecord[], sort: SowSort): SowListRecord[] {
  const t = (s?: string) => (s ? new Date(s).getTime() || 0 : 0);
  const by: Record<SowSort, (a: SowListRecord, b: SowListRecord) => number> = {
    updated: (a, b) => t(b.updatedAt || b.createdAt) - t(a.updatedAt || a.createdAt),
    client: (a, b) => (a.companyName ?? '').localeCompare(b.companyName ?? '') || (a.name ?? '').localeCompare(b.name ?? ''),
    status: (a, b) => STATUS_ORDER.indexOf(a.status ?? 'draft') - STATUS_ORDER.indexOf(b.status ?? 'draft') || t(b.updatedAt || b.createdAt) - t(a.updatedAt || a.createdAt),
    value: (a, b) => sowValue(b) - sowValue(a),
    start: (a, b) => t(b.timeline?.startDate) - t(a.timeline?.startDate),
  };
  return [...sows].sort(by[sort]);
}

export function progressOf(sow: SowListRecord): number {
  const d = sow.deliverables ?? [];
  if (d.length === 0) return 0;
  return Math.round((d.filter((x) => x.status === 'completed').length / d.length) * 100);
}
